import { Phone } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLang } from "@/i18n/LanguageContext";
import { brand, gallery, waLink } from "@/content/site";
import { Reveal, SectionHead, Ltr, WhatsAppIcon } from "@/components/apex/primitives";

const Faq = () => {
  const { t, isRtl } = useLang();
  const img = gallery.find((g) => g.id === "apex-glass-line") || gallery[0];

  return (
    <section id="faq" data-testid="faq-section" className="relative py-14 sm:py-20">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <SectionHead align="center" label={t.faq.label} title={t.faq.title} />

        <div className="mt-10 grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-12">
          <Reveal>
            <div className="space-y-5 lg:sticky lg:top-28">
              <div className="ap-img aspect-[4/3]">
                <img
                  src={img.src}
                  alt={isRtl ? img.alt_ar : img.alt_en}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                  style={{ objectPosition: "center 30%" }}
                />
              </div>
              <div className="ap-dark rounded-[var(--r)] p-7">
                <h3 className="ap-display text-xl text-white">{t.contact.title}</h3>
                <p className="mt-3 text-[13.5px] leading-relaxed text-white/60">{t.contact.body}</p>
                <div className="mt-6 space-y-3">
                  <a
                    href={brand.telUrl}
                    data-testid="faq-phone"
                    className="group flex items-center gap-4 rounded-2xl bg-white/[0.06] px-4 py-3 transition-colors hover:bg-white/10"
                  >
                    <span className="ap-icon h-11 w-11">
                      <Phone className="h-4.5 w-4.5" strokeWidth={2} />
                    </span>
                    <span>
                      <span className="block text-[12px] text-white/55">{t.contact.call}</span>
                      <Ltr className="ap-mono text-[15px] font-semibold text-white">{brand.phoneDisplay}</Ltr>
                    </span>
                  </a>
                  <a
                    href={waLink(t.float.defaultMsg)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="faq-whatsapp"
                    className="group flex items-center gap-4 rounded-2xl bg-white/[0.06] px-4 py-3 transition-colors hover:bg-white/10"
                  >
                    <span className="ap-icon h-11 w-11 !bg-[var(--wa)] !text-white">
                      <WhatsAppIcon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block text-[12px] text-white/55">WhatsApp</span>
                      <span className="text-[15px] font-semibold text-white">{t.contact.wa}</span>
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <Accordion type="single" collapsible defaultValue="faq-0" className="space-y-3">
              {t.faq.items.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="ap-card overflow-hidden border-0 px-6 data-[state=open]:shadow-[0_24px_50px_-30px_rgba(21,21,21,0.3)]"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="gap-6 py-5 text-start text-[15.5px] font-semibold text-[var(--text)] hover:text-[var(--yellow-dk)] hover:no-underline [&>svg]:h-5 [&>svg]:w-5 [&>svg]:rounded-full [&>svg]:bg-[var(--cream-2)] [&>svg]:p-0.5 [&>svg]:text-[var(--text)]"
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent data-testid={`faq-content-${i}`} className="pb-6 text-[14px] leading-[1.9] text-[var(--muted)]">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Faq;
